/**
 * ContactQuickCreateDialog - Minimal dialog for quick contact creation
 *
 * Following the Quick Create Dialog pattern:
 * - Fields: First Name, Last Name, Email, Mobile, Job Title, Organization, Relationship
 * - When company is selected, copies company's relationships as defaults
 * - Redirects to detail page after creation for full editing
 *
 * **Mobile, not phone** (WI 08ab4081): the one number a quick create can afford
 * to ask for is the one the user will actually reach the person on, and for a
 * B2B contact that is now the mobile. The landline stays on the detail page.
 * Both feed the same duplicate-check tier, so the preflight probe moved with it.
 *
 * The relationship control offers the tenant's own relationship CLASSES beside
 * the built-in enum — see `RelationshipChipPicker`.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { useApiError } from '../../../../shared/hooks/useApiError';
import { getFieldErrors } from '../../../../shared/utils/errors';
import {
  CustomFieldsSection,
  activeCustomFieldDefinitions,
  relationshipScopeTokens,
  useCustomFieldDefinitions,
  validateCustomFields,
  visibleCustomFieldDefinitions,
  type CustomFieldErrors,
} from '../../../../shared/components/customFields';
import { useDuplicateCheck } from '../hooks/useDuplicateCheck';
import { DuplicateAlert } from './DuplicateAlert';
import { cn } from '../../../../lib/utils';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { DialogForm, ValidatedEmailInput, ValidatedPhoneInput } from '../../../../shared/components/common';
import { useCreateContact } from '../hooks/useContacts';
import { useOrganization, useOrganizationDomainMatch } from '../hooks/useOrganizations';
import { OrganizationPicker } from './OrganizationPicker';
import { OrganizationDomainSuggestion } from './OrganizationDomainSuggestion';
import { RelationshipChipPicker } from './RelationshipChipPicker';
import type { Contact, ContactCreate, Organization } from '../types';

const EMPTY_RELATIONSHIPS: string[] = [];

/** How the backend names a custom-field rejection (`field="custom_fields.<key>"`). */
const CUSTOM_FIELD_ERROR_PREFIX = 'custom_fields.';

interface ContactQuickCreateDialogProps {
  open: boolean;
  onClose: () => void;
  /** Pre-fill company from context */
  defaultCompanyId?: string;
  /** Pre-fill the first name field (e.g., from picker search text) */
  defaultFirstName?: string;
  /** Pre-fill the last name field (e.g., from picker search text) */
  defaultLastName?: string;
  /**
   * Pre-fill the email field. The caller sometimes knows the address before it
   * knows the person — an org-level `email_addresses` entry the buyer is about to
   * invite to the portal — and re-typing it into the new contact is where that
   * flow used to dead-end.
   */
  defaultEmail?: string;
  /** Callback when contact is created (before navigation) */
  onCreated?: (contact: Contact) => void;
  /** If true, don't navigate to detail page after creation */
  skipNavigation?: boolean;
  /** Pre-select these contact relationship types (e.g., ['lead'] or ['customer']) */
  defaultRelationships?: string[];
}

interface QuickCreateFormData {
  first_name: string;
  last_name: string;
  email: string;
  mobile: string;
  title: string;
  organization_id?: string;
  contact_relationship: string[];
  /** Tenant relationship-class ids — a separate stored dimension from the enum. */
  class_ids: string[];
}

export function ContactQuickCreateDialog({
  open,
  onClose,
  defaultCompanyId,
  defaultFirstName = '',
  defaultLastName = '',
  defaultEmail = '',
  onCreated,
  skipNavigation = false,
  defaultRelationships = EMPTY_RELATIONSHIPS,
}: ContactQuickCreateDialogProps) {
  const { t } = useTranslation(['contacts', 'common']);
  const { t: tcf } = useTranslation('custom_fields');
  const navigate = useNavigate();
  const createMutation = useCreateContact();
  const userHasEditedRelationships = useRef(false);
  const { handleMutationError } = useApiError({ showToast: false });

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<QuickCreateFormData>({
    mode: 'onBlur',
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      mobile: '',
      title: '',
      organization_id: defaultCompanyId || undefined,
      contact_relationship: defaultRelationships,
      class_ids: [],
    },
  });

  const contactRelationship = watch('contact_relationship');
  const classIds = watch('class_ids');
  const emailValue = watch('email');
  const organizationId = watch('organization_id');
  const [dismissedDomain, setDismissedDomain] = useState('');
  const mobileValue = watch('mobile');
  const firstNameValue = watch('first_name');
  const lastNameValue = watch('last_name');

  // Contact custom fields, REQUIRED ones only. `POST /contacts` validates
  // `partial=False`, so a tenant's required field is not something the detail page
  // can collect afterwards — without it the create is refused, and this dialog is
  // the only create surface some entry points have. The optional ones stay off the
  // dialog: that is what keeps the quick-create budget honest
  // (`.claude/rules/rules-ui-dialogs.md`) while the mandatory ones are askable at all.
  const [customFields, setCustomFields] = useState<Record<string, unknown>>({});
  const [customFieldErrors, setCustomFieldErrors] = useState<CustomFieldErrors>({});
  const { data: cfData } = useCustomFieldDefinitions('contact');
  // The scope the backend will validate against — the union of the contact's
  // built-in relationships and the tenant classes picked in the same control,
  // mirroring `relationship_scope_tokens(contact_relationship, class_ids)` on the
  // create route. Both arms matter: a field scoped to a tenant class is required
  // the moment that class is ticked here, and scoping on the built-ins alone
  // would hide it until the create came back 422.
  const cfRelationships = useMemo(
    () => relationshipScopeTokens(contactRelationship, classIds),
    [contactRelationship, classIds]
  );
  const requiredCfDefinitions = useMemo(
    () =>
      visibleCustomFieldDefinitions(
        activeCustomFieldDefinitions(cfData?.definitions),
        cfRelationships
      ).filter((d) => d.required),
    [cfData?.definitions, cfRelationships]
  );

  // Preflight the tiered duplicate check while typing. This dialog is the
  // primary create path from the list page, so without it the only feedback on
  // a duplicate is the 409 the save is rejected with.
  const { match: duplicateMatch } = useDuplicateCheck(
    open
      ? {
          entity_type: 'contacts',
          email: emailValue || undefined,
          mobile: mobileValue || undefined,
          first_name: firstNameValue || undefined,
          last_name: lastNameValue || undefined,
        }
      : null,
  );

  // Suggest an existing organization matching the email domain (fred@acme.com
  // → acme.com). Only while the dialog is open and no organization is set yet.
  const { match: domainMatch, domain: matchedDomain } = useOrganizationDomainMatch(
    emailValue,
    open && !organizationId,
  );
  const showDomainSuggestion =
    !organizationId && !!domainMatch && matchedDomain !== dismissedDomain;

  // When the contact is being created within an organization (org pre-filled via
  // defaultCompanyId), fetch that org so the contact inherits its relationships.
  // The interactive picker path is handled separately by handleCompanySelected.
  const { data: defaultOrg } = useOrganization(open && defaultCompanyId ? defaultCompanyId : '');

  useEffect(() => {
    if (open) {
      reset({
        first_name: defaultFirstName,
        last_name: defaultLastName,
        email: defaultEmail,
        mobile: '',
        title: '',
        organization_id: defaultCompanyId || undefined,
        contact_relationship: defaultRelationships,
        class_ids: [],
      });
      userHasEditedRelationships.current = false;
      setDismissedDomain('');
      setCustomFields({});
      setCustomFieldErrors({});
    }
  }, [open, defaultCompanyId, defaultFirstName, defaultLastName, defaultEmail, defaultRelationships, reset]);

  // Inherit relationships from the pre-filled organization (default behavior:
  // a contact created within an org adopts the org's relationships). Skipped
  // once the user edits the relationships manually.
  useEffect(() => {
    if (
      open &&
      defaultCompanyId &&
      defaultOrg?.organization_relationship?.length &&
      !userHasEditedRelationships.current
    ) {
      const merged = Array.from(
        new Set([...defaultRelationships, ...defaultOrg.organization_relationship]),
      );
      setValue('contact_relationship', merged);
    }
  }, [open, defaultCompanyId, defaultOrg, defaultRelationships, setValue]);

  const handleCompanySelected = (company: Organization | null) => {
    if (company && company.organization_relationship?.length > 0 && !userHasEditedRelationships.current) {
      // Copy company relationships as defaults, preserving any defaultRelationships
      const merged = Array.from(new Set([...defaultRelationships, ...company.organization_relationship]));
      setValue('contact_relationship', merged);
    }
  };

  const handleUseSuggestedOrganization = (company: Organization) => {
    setValue('organization_id', company.id);
    handleCompanySelected(company);
  };

  // The picker hands back the whole next array. Any edit here stops the
  // organization's relationships being copied over the user's choice.
  const handleRelationshipChange = (next: string[]) => {
    userHasEditedRelationships.current = true;
    setValue('contact_relationship', next);
  };

  // `handleMutationError` files every field error the envelope names; this drops
  // the custom-field ones on the floor so they never reach react-hook-form (they
  // are rendered by `CustomFieldsSection` instead — see the catch block below).
  const setNonCustomFieldError: typeof setError = (name, error, options) => {
    if (String(name).startsWith(CUSTOM_FIELD_ERROR_PREFIX)) return;
    setError(name, error, options);
  };

  const onSubmit = async (data: QuickCreateFormData) => {
    const cfErrors = validateCustomFields(requiredCfDefinitions, customFields, tcf);
    setCustomFieldErrors(cfErrors);
    if (Object.keys(cfErrors).length > 0) return;

    try {
      const createData: ContactCreate = {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email || undefined,
        mobile: data.mobile?.trim() || undefined,
        title: data.title?.trim() || undefined,
        organization_id: data.organization_id || undefined,
        contact_relationship: data.contact_relationship.length > 0 ? data.contact_relationship : undefined,
        class_ids: data.class_ids.length > 0 ? data.class_ids : undefined,
        // A tenant with no required field sends exactly the payload it did before.
        ...(requiredCfDefinitions.length > 0 ? { custom_fields: customFields } : {}),
      };

      const newContact = await createMutation.mutateAsync(createData);
      onCreated?.(newContact);
      onClose();

      if (!skipNavigation) {
        navigate(`/master-data/contacts/${newContact.id}`);
      }
    } catch (error) {
      // A custom-field rejection names `custom_fields.<key>`, which is no field of
      // this form. Route those onto the inputs that own them — and keep them OUT
      // of react-hook-form: an error set on a name the form never registered is
      // never cleared by validating the fields it did, so `handleSubmit` refuses
      // every later submit. The user then fixes the field and Create does
      // nothing, for the rest of the dialog's life.
      const cfErrorsFromServer: CustomFieldErrors = {};
      for (const [field, message] of Object.entries(getFieldErrors(error) ?? {})) {
        if (field.startsWith(CUSTOM_FIELD_ERROR_PREFIX)) {
          cfErrorsFromServer[field.slice(CUSTOM_FIELD_ERROR_PREFIX.length)] = message;
        }
      }
      setCustomFieldErrors(cfErrorsFromServer);
      // The global mutation-error handler shows the translated toast; this
      // paints the offending field (e.g. a duplicate email) red as well.
      handleMutationError(error, setNonCustomFieldError);
    }
  };

  return (
    <DialogForm
      open={open}
      onClose={onClose}
      title={t('contacts.create')}
      onSubmit={handleSubmit(onSubmit)}
      submitLabel={t('common:app.create')}
      isLoading={isSubmitting}
      // `md`, not the `sm` default: `ui-dialogs.md` § 2.1 says sm is for 3-4
      // fields and md for "5+ or complex pickers", and this dialog has seven
      // controls including the organization picker. At `sm` the eight
      // relationship chips wrap to three rows, which is what pushed the custom
      // relationship section below the dialog's scroll fold at 1440x900 — i.e.
      // the feature was there and invisible until you scrolled (WI 08ab4081).
      maxWidth="md"
    >
      <div className="space-y-4">
        <DuplicateAlert match={duplicateMatch} entityType="contacts" />
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="first_name">{t('contacts.firstName')}</Label>
            <Controller
              name="first_name"
              control={control}
              rules={{
                validate: (value, formValues) =>
                  (value || '').trim() || (formValues.last_name || '').trim()
                    ? true
                    : t('common:validation.firstOrLastNameRequired'),
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  id="first_name"
                  autoFocus
                  className={cn(errors.first_name && 'border-destructive')}
                />
              )}
            />
            {errors.first_name && (
              <p className="text-sm text-destructive">{errors.first_name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="last_name">{t('contacts.lastName')}</Label>
            <Controller
              name="last_name"
              control={control}
              rules={{
                validate: (value, formValues) =>
                  (value || '').trim() || (formValues.first_name || '').trim()
                    ? true
                    : t('common:validation.firstOrLastNameRequired'),
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  id="last_name"
                  className={cn(errors.last_name && 'border-destructive')}
                />
              )}
            />
            {errors.last_name && (
              <p className="text-sm text-destructive">{errors.last_name.message}</p>
            )}
          </div>
        </div>

        <ValidatedEmailInput
          name="email"
          control={control}
          label={t('contacts.email')}
        />

        <ValidatedPhoneInput
          name="mobile"
          control={control}
          label={t('contacts.mobile')}
        />

        <div className="space-y-2">
          <Label htmlFor="title">{t('contacts.jobTitle')}</Label>
          <Controller
            name="title"
            control={control}
            render={({ field }) => <Input {...field} id="title" />}
          />
        </div>

        <div className="space-y-2">
          <Label>{t('contacts.company')}</Label>
          <Controller
            name="organization_id"
            control={control}
            render={({ field }) => (
              <OrganizationPicker
                value={field.value || ''}
                onChange={(id) => field.onChange(id || undefined)}
                onCompanySelected={handleCompanySelected}
                defaultRelationships={contactRelationship}
                allowClear
              />
            )}
          />
          {showDomainSuggestion && domainMatch && (
            <OrganizationDomainSuggestion
              organization={domainMatch}
              domain={matchedDomain}
              onUse={handleUseSuggestedOrganization}
              onDismiss={() => setDismissedDomain(matchedDomain)}
            />
          )}
        </div>

        {/* Both relationship vocabularies — the built-in enum and the tenant's own
            classes — in ONE control, because they answer one question about the
            record (WI 08ab4081). Two stored dimensions, hence two callbacks. */}
        <RelationshipChipPicker
          entity="contact"
          value={contactRelationship || []}
          onChange={handleRelationshipChange}
          classValue={classIds || []}
          onClassChange={(next) => setValue('class_ids', next)}
        />

        {/* Only the tenant's REQUIRED contact custom fields — the create endpoint
            refuses without them, so they are part of the minimum this dialog owes,
            not the optional detail the detail page collects
            (`.claude/rules/rules-ui-dialogs.md`). Scoped by the relationships
            selected above, exactly as the backend scopes its own validation. */}
        {requiredCfDefinitions.length > 0 && (
          <CustomFieldsSection
            entityType="contact"
            requiredOnly
            values={customFields}
            onChange={setCustomFields}
            errors={customFieldErrors}
            relationships={cfRelationships}
          />
        )}

        <p className="text-xs text-muted-foreground">
          {t('contacts.quickCreateHint')}
        </p>
      </div>
    </DialogForm>
  );
}
